import React, { useState, useEffect } from 'react';
import API from '../utils/api';
import { Box, Snackbar, Alert, Button, IconButton } from '@mui/material';
import { Close as CloseIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  requestPushPermission,
  ensurePushSubscription,
  getNotificationPermission,
  isStandalonePwa,
  PUSH_SETUP_OK_KEY,
} from '../services/pushNotifications';

export async function enablePushFromUserTap() {
  const sub = await requestPushPermission();
  const permission = getNotificationPermission();
  if (permission === 'granted' && sub) {
    try {
      await API.post('pushnotifications/test/');
      localStorage.setItem(PUSH_SETUP_OK_KEY, '1');
      toast.success(
        'Notifications on. Lock the phone or switch apps to see the test. @mentions notify the other person’s device, not yours.'
      );
    } catch {
      toast.success('Notifications enabled. If no banner appears, the server could not send a test push.');
    }
    return { ok: true, permission, sub };
  }
  if (permission === 'granted' && !sub) {
    toast.error('Permission is on, but subscription failed. Check VAPID keys and reload.');
    return { ok: false, permission, sub: null };
  }
  if (permission === 'denied') {
    toast.warning('Notifications were blocked. Enable them in system Settings, then reopen the app.');
    return { ok: false, permission, sub: null };
  }
  toast.info('Allow notifications on the system prompt to finish setup.');
  return { ok: false, permission, sub: null };
}

export default function PushNotificationPrompt() {
  const [showPrompt, setShowPrompt] = useState(() => {
    try {
      return !localStorage.getItem(PUSH_SETUP_OK_KEY);
    } catch {
      return true;
    }
  });
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const permission = getNotificationPermission();
    if (permission === 'unsupported') {
      setShowPrompt(false);
      return;
    }
    if (permission === 'denied') {
      setDenied(true);
    }
    if (permission === 'granted') {
      ensurePushSubscription().catch(() => {});
    }
    if (!localStorage.getItem(PUSH_SETUP_OK_KEY)) {
      setShowPrompt(true);
    }
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const result = await enablePushFromUserTap();
      if (result.permission === 'denied') setDenied(true);
      if (result.ok) setShowPrompt(false);
    } catch (err) {
      if (err?.code === 'missing_vapid_public_key') {
        toast.error('Push is not configured (missing public VAPID key in the app build).');
        return;
      }
      toast.error('Could not enable notifications. Try again from this home-screen app.');
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  const standalone = isStandalonePwa();

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 88, sm: 24 }, zIndex: 10000 }}
    >
      <Alert
        severity={denied ? 'warning' : 'info'}
        icon={<NotificationsIcon />}
        action={
          <>
            {!denied && (
              <Button
                color="inherit"
                size="small"
                disabled={busy}
                onClick={handleEnable}
              >
                {busy ? 'Enabling…' : 'Enable'}
              </Button>
            )}
            <IconButton size="small" onClick={handleDismiss} aria-label="dismiss">
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        sx={{ width: '100%', maxWidth: 520 }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.25 }}>Enable notifications</Box>
        <Box sx={{ fontSize: '0.875rem' }}>
          {denied
            ? 'Notifications are blocked. Allow them in system Settings, then open this home-screen app again.'
            : standalone
              ? 'Tap Enable to finish setup on this device (needed even if notifications are already on in Settings).'
              : 'Tap Enable to finish setup. On iPhone, use the home-screen app (not the Safari tab).'}
        </Box>
      </Alert>
    </Snackbar>
  );
}
